import type { IConsumable } from '@experiences/interfaces';
import { UiText } from '@experiences/ui-common';
import {
    getShortNumber,
    useLocalizedLinks,
} from '@experiences/util';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    Spacing,
    Typography,
} from '@uipath/apollo-core';
import React, { useMemo } from 'react';
import {
    FormattedDate,
    FormattedTime,
    useIntl,
} from 'react-intl';

import {
    consumableCodeToApiKeyIdentifier,
    consumableToLearnMoreLink,
} from '../../../../common/constants/Constant';
import { useConsumptionDashboardLink } from '../../helperComponents/UiConsumableCard/useConsumptionDashboardLink';
import { useViewUsageTelemetry } from '../../helperComponents/UiConsumableCard/useViewUsageTelemetry';
import { consumableCardFrame } from './cardStyles';
import { getConsumableBundleRange } from './consumableBundleRange';
import {
    computeConsumableStatus,
    getConsumableStatusNote,
} from './statusModel';
import { UiApiKeySecretV2 } from './UiApiKeySecretV2';

const useStyles = makeStyles(theme =>
    createStyles({
        card: {
            ...consumableCardFrame(theme),
            gap: Spacing.SpacingMicro,
            height: '100%',
        },
        expandedCard: { borderColor: theme.palette.semantic.colorPrimary },
        titleGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingMicro,
        },
        header: {
            fontSize: Typography.fontSizeMBold.fontSize,
            lineHeight: Typography.fontSizeMBold.lineHeight,
            fontWeight: Typography.fontSizeMBold.fontWeight,
            color: theme.palette.semantic.colorForeground,
        },
        learnMoreIcon: {
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: Typography.fontSizeM.fontSize,
            color: theme.palette.semantic.colorForegroundDeEmp,
            cursor: 'pointer',
        },
        subtitle: {
            fontSize: Typography.fontSizeS.fontSize,
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        bigNumberRow: {
            display: 'flex',
            alignItems: 'baseline',
            gap: Spacing.SpacingMicro,
            marginTop: Spacing.SpacingS,
        },
        bigNumber: {
            fontSize: '22px',
            fontWeight: 700,
            lineHeight: '1',
            color: theme.palette.semantic.colorForeground,
        },
        bigNumberRest: {
            fontSize: '13px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        progressRow: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingS,
            marginTop: Spacing.SpacingXs,
        },
        progressBar: {
            flex: 1,
            height: Spacing.SpacingXs,
            borderRadius: Border.BorderRadiusNone,
        },
        progressBarColorPrimary: { backgroundColor: theme.palette.semantic.colorBackgroundDisabled },
        progressBarBar: { backgroundColor: theme.palette.semantic.colorPrimary },
        progressPercent: {
            flex: 'none',
            fontSize: '13px',
            color: theme.palette.semantic.colorForeground,
        },
        noteWarning: {
            fontSize: Typography.fontSizeS.fontSize,
            color: theme.palette.semantic.colorWarningText,
            marginTop: Spacing.SpacingMicro,
        },
        noteCritical: {
            fontSize: Typography.fontSizeS.fontSize,
            color: theme.palette.semantic.colorErrorText,
            marginTop: Spacing.SpacingMicro,
        },
        refreshNote: {
            fontSize: Typography.fontSizeS.fontSize,
            color: theme.palette.semantic.colorForegroundDeEmp,
            marginTop: Spacing.SpacingMicro,
        },
        apiKeySection: {
            marginTop: 'auto',
            paddingTop: Spacing.SpacingXs,
            borderTop: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
        },
        divider: {
            borderTop: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
            paddingTop: Spacing.SpacingXs,
        },
        dividerPinnedToBottom: { marginTop: 'auto' },
        footer: {
            display: 'flex',
            alignItems: 'center',
            fontSize: Typography.fontSizeS.fontSize,
        },
        footerLeft: { color: theme.palette.semantic.colorForegroundDeEmp },
        footerLinks: {
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingM,
        },
        link: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: Spacing.SpacingMicro,
            color: theme.palette.semantic.colorForegroundLink,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px',
            '&:hover': { textDecoration: 'underline' },
        },
        chevron: {
            fontSize: Typography.fontSizeL.fontSize,
            transition: 'transform .15s',
        },
        chevronExpanded: { transform: 'rotate(180deg)' },
    }),
);

export const UiConsumableCardV2: React.FC<{
    consumable: IConsumable;
    tenantId?: string;
    // Free, self-refreshing allowances ("Bundled with organization licenses"): same card, but no
    // API key and no dashboard link.
    isFreeConsumable?: boolean;
    overviewExpanded: boolean;
    onToggleOverview: () => void;
}> = ({
    consumable, tenantId, isFreeConsumable = false, overviewExpanded, onToggleOverview,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const getLocalizedLink = useLocalizedLinks();
    const {
        hasConsumptionDashboard, enableConsumptionDashboard, consumptionDashboardHref,
    } = useConsumptionDashboardLink(consumable.code, tenantId);

    // "Overview" succeeds the legacy "View Usage" action — reuses its telemetry event.
    const handleTelemetryForOverview = useViewUsageTelemetry(consumable.code, tenantId);

    const shouldHaveApiKey = useMemo(() =>
        !isFreeConsumable && consumableCodeToApiKeyIdentifier[consumable.code] !== undefined
    , [ consumable.code, isFreeConsumable ]);

    // Free pools refresh on a cadence; say so, with the next reset, the way the legacy card did.
    const refreshNote = useMemo(() => {
        const refreshingPool = consumable.pools.find(pool => pool.refreshType && pool.hasNextAvailable);
        if (!refreshingPool?.refreshType || !refreshingPool.hasNextAvailable) {
            return undefined;
        }
        const nextAvailableDate = new Date(refreshingPool.hasNextAvailable);
        return translate(
            { id: `CLIENT_CONSUMABLE_REFRESH_${refreshingPool.refreshType.toUpperCase()}` },
            {
                date: <FormattedDate
                    value={nextAvailableDate}
                    year="numeric"
                    month="short"
                    day="numeric" />,
                time: <FormattedTime
                    value={nextAvailableDate}
                    hour="2-digit"
                    minute="2-digit"
                    timeZoneName="short" />,
            },
        );
    }, [ consumable.pools, translate ]);

    const {
        totalConsumed, totalCapacity,
    } = useMemo(() => ({
        totalConsumed: consumable.pools.reduce((sum, pool) => sum + pool.consumed, 0),
        totalCapacity: consumable.pools.reduce((sum, pool) => sum + pool.total, 0),
    }), [ consumable.pools ]);
    const {
        bundleStartDate, bundleEndDate,
    } = useMemo(() => getConsumableBundleRange(consumable), [ consumable ]);

    const status = useMemo(() => computeConsumableStatus(totalConsumed, totalCapacity), [ totalConsumed, totalCapacity ]);
    const note = useMemo(() => getConsumableStatusNote(totalConsumed, totalCapacity), [ totalConsumed, totalCapacity ]);
    // total=0 makes the ratio undefined — treat nonzero consumption there as fully consumed (100%)
    // rather than showing a misleading 0%.
    const consumedPercent = totalCapacity > 0
        ? Math.round((totalConsumed / totalCapacity) * 100)
        : totalConsumed > 0 ? 100 : 0;
    const cappedPercent = Math.min(consumedPercent, 100);
    const availableText = translate(
        { id: 'CLIENT_CONSUMABLE_V2_AVAILABLE' },
        { 0: getShortNumber(Math.max(totalCapacity - totalConsumed, 0)) },
    );

    // Exact (not short-formatted) numbers, matching the legacy progress bar tooltip.
    const progressTooltipConsumedText = translate({ id: 'CLIENT_CONSUMABLE_TOOLTIP_CONSUMED' }, { 0: totalConsumed });
    const progressTooltipAvailableText = translate(
        { id: 'CLIENT_CONSUMABLE_TOOLTIP_AVAILABLE' },
        { 0: Math.max(totalCapacity - totalConsumed, 0) },
    );
    const progressTooltipOverconsumptionText = translate(
        { id: 'CLIENT_CONSUMABLE_TOOLTIP_OVERCONSUMPTION' },
        { 0: Math.max(totalConsumed - totalCapacity, 0) },
    );

    return (
        <div
            className={`${classes.card} ${overviewExpanded ? classes.expandedCard : ''}`}
            data-cy={`consumables-v2-card-${consumable.code.toLowerCase()}`}>
            <div className={classes.titleGroup}>
                <UiText
                    className={classes.header}
                    role="heading"
                    aria-level={2}>
                    {translate({ id: `CLIENT_CONSUMABLE_USAGE_HEADER_${consumable.code}` })}
                </UiText>
                {consumableToLearnMoreLink[consumable.code] && (
                    <Tooltip title={translate({ id: 'CLIENT_LEARN_MORE' })}>
                        <a
                            className={classes.learnMoreIcon}
                            target="_blank"
                            rel="noreferrer"
                            href={getLocalizedLink(consumableToLearnMoreLink[consumable.code])}
                            aria-label={translate({ id: 'CLIENT_LEARN_MORE' })}
                            data-cy="consumables-v2-learn-more-icon"
                        >
                            <InfoOutlinedIcon fontSize="inherit" />
                        </a>
                    </Tooltip>
                )}
            </div>

            {bundleStartDate != null && bundleEndDate != null && (
                <UiText className={classes.subtitle}>
                    <FormattedDate
                        value={bundleStartDate * 1000}
                        year="numeric"
                        month="short"
                        day="numeric"
                        timeZone="UTC" />
                    {' – '}
                    <FormattedDate
                        value={bundleEndDate * 1000}
                        year="numeric"
                        month="short"
                        day="numeric"
                        timeZone="UTC" />
                </UiText>
            )}

            <div className={classes.bigNumberRow}>
                <span className={classes.bigNumber}>
                    {getShortNumber(totalConsumed)}
                </span>
                <span className={classes.bigNumberRest}>
                    {`/ ${translate({ id: 'CLIENT_CONSUMED_ON_TENANT_QTY' }, { 0: getShortNumber(totalCapacity) })}`}
                </span>
            </div>

            <Tooltip
                title={
                    <>
                        <p>
                            {progressTooltipConsumedText}
                        </p>
                        <p>
                            {progressTooltipAvailableText}
                        </p>
                        {totalConsumed > totalCapacity && <p>
                            {progressTooltipOverconsumptionText}
                        </p>}
                    </>
                }>
                <div className={classes.progressRow}>
                    <LinearProgress
                        className={classes.progressBar}
                        classes={{
                            colorPrimary: classes.progressBarColorPrimary,
                            bar: classes.progressBarBar,
                        }}
                        variant="determinate"
                        value={cappedPercent}
                        aria-label={progressTooltipConsumedText}
                        aria-valuetext={`${progressTooltipConsumedText} ${progressTooltipAvailableText}`} />
                    <span className={classes.progressPercent}>
                        {`${consumedPercent}%`}
                    </span>
                </div>
            </Tooltip>

            {note && (
                <UiText className={status === 'critical' ? classes.noteCritical : classes.noteWarning}>
                    {translate({ id: note.id }, note.overage != null ? { 0: getShortNumber(note.overage) } : undefined)}
                </UiText>
            )}

            {refreshNote && (
                <UiText
                    className={classes.refreshNote}
                    data-cy={`consumables-v2-refresh-note-${consumable.code.toLowerCase()}`}>
                    {refreshNote}
                </UiText>
            )}

            {shouldHaveApiKey && (
                <div className={classes.apiKeySection}>
                    <UiApiKeySecretV2
                        consumableCode={consumable.code}
                        apiKey={consumable.apiKey}
                        tenantId={tenantId} />
                </div>
            )}

            <div className={`${classes.divider} ${shouldHaveApiKey ? '' : classes.dividerPinnedToBottom}`}>
                <div className={classes.footer}>
                    <span className={classes.footerLeft}>
                        {availableText}
                    </span>
                    <div className={classes.footerLinks}>
                        <Link
                            component="button"
                            type="button"
                            data-cy={`consumables-v2-overview-link-${consumable.code}`}
                            className={classes.link}
                            onClick={() => {
                                onToggleOverview();
                                handleTelemetryForOverview();
                            }}
                            underline='none'
                            aria-expanded={overviewExpanded}>
                            {translate({ id: 'CLIENT_CONSUMABLE_V2_OVERVIEW' })}
                            <ExpandMoreIcon
                                className={`${classes.chevron} ${overviewExpanded ? classes.chevronExpanded : ''}`}
                                color="primary" />
                        </Link>
                        {!isFreeConsumable && hasConsumptionDashboard && enableConsumptionDashboard &&
                            <Link
                                data-cy={`consumables-v2-view-details-link-${consumable.code}`}
                                className={classes.link}
                                href={consumptionDashboardHref}
                                underline='none'>
                                {translate({ id: 'CLIENT_CONSUMABLE_VIEW_USAGE_PREVIEW' })}
                            </Link>}
                    </div>
                </div>
            </div>
        </div>
    );
};
